import { routeTree } from 'virtual:boot-route-tree';
import { NotFound } from '@client/components/NotFound';
import { trackNavigationSideEffects } from '@client/hooks/navigation/navigation-side-effects';
import { trackPreviousPathname } from '@client/hooks/navigation/previous-pathname';
import { AuthCallback, Route as AuthCallbackRoute } from '@client/routes/~(authentication)/~callback';
import { theme } from '@client/styles/theme';
import { trpcQueryUtils } from '@client/trpc/client';
import type { CommonBreadCrumbItem } from '@client/utils';
import { getFeatureFlagClient } from '@client/utils/feature-flags';
import { attachRoutePreloader } from '@client/utils/route-preloader';
import { attachDeferredRoutes } from '@client/utils/routing/deferred-route-loader';
import { parseSearch } from '@client/utils/routing/parse-search';
import { trackIdleSchedulingNavigation } from '@client/utils/scheduling/scheduleOnIdle';
import { trackRouteChunkWarmth } from '@client/utils/telemetry/route-chunk-telemetry';
import { trackSecretLocationRecordingGate } from '@client/utils/telemetry/secret-location-recording-gate';
import type { AppModule, Role, UserFlag } from '@shared/constants';
import type { FormStateEntity } from '@shared/constants/business/entity';
import type { Permission } from '@shared/permissions/permissions';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { PuffLoader } from 'react-spinners';

AuthCallbackRoute.update({
    component: AuthCallback,
});

// Create the feature flag client once at module level
const featureFlagClient = getFeatureFlagClient();

// The router starts with the boot tree, which leaves out the deferred route areas (the admin
// console): their stubs, wiring and preload lists stay out of the entry chunk. Those areas join
// the same root route on demand (see utils/routing/deferred-route-loader.ts).
export const router = createRouter({
    routeTree,
    defaultNotFoundComponent: NotFound,
    defaultPreload: 'intent',
    // The default parser, keeping bigint ids in the URL exact (see parse-search.ts).
    parseSearch,
    // TanStack Router pattern: These are initialized with undefined! as placeholders.
    // Actual values are provided via RouterProvider's context prop at runtime.
    // See: https://tanstack.com/router/latest/docs/framework/react/guide/router-context
    context: {
        trpcUtils: undefined!,
        featureFlagClient: undefined!,
    },
    defaultPendingComponent: () => {
        return (
            <div className="crm-pending-component inset-0 flex items-center justify-center">
                <div className="min-h-[60px] py-10">
                    <PuffLoader color={theme?.token?.colorPrimary} size={40} />
                </div>
            </div>
        );
    },
});

attachDeferredRoutes(router);

// Lets a route's `beforeLoad`, which is not handed the router, start loading the chunks of the
// routes it is about to redirect to (see `startRouteChunkPreloads`).
attachRoutePreloader(router);

// Path-state cleanup and the pending silent update, both keyed off the router event that survives
// a redirect (see `trackNavigationSideEffects` — the choice of event is load-bearing and tested).
trackNavigationSideEffects(router);

// Record the pathname each navigation came from, so a list page that remounts on return can tell a
// fresh entry from a return out of one of its own sub-routes (see saved-views built-in handling).
trackPreviousPathname(router);

// Keep late-mount idle work on the cold-load path until the first subsequent
// client-side navigation begins; `document.readyState` cannot distinguish it.
trackIdleSchedulingNavigation(router);

// Per navigation, whether the destination's code was already loaded and how long it took: the
// measure of what the route preloaders buy. Registered after the tracker above, whose signal
// it reads on the same event.
trackRouteChunkWarmth(router);

// Session recording must never capture an access-key page or an invite link reached by in-app
// navigation; the direct-load case is gated where each recorder starts.
trackSecretLocationRecordingGate(router);

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router;
    }

    interface StaticDataRouteOption {
        /**
         * Controls how a route is *presented* within the main app layout.
         *
         * - `page` is the normal in-layout page rendering
         * - `overlay` is for drawers/modals routed "on top" of a background page.
         *   Overlay routes are intentionally ignored by breadcrumbs/title/icon/layout-variant resolution.
         * - `fullPage` is for full-page takeover routes. `PageController` portals the route's content into the
         *   `#page-layout-full-page-container` element owned by `PageLayout`.
         *
         * @default page
         */
        presentation?: 'page' | 'overlay' | 'fullPage';
        /**
         * Metadata is used for the page title in the browser tab. It also serves as the
         * breadcrumb-title fallback for routes whose `breadcrumb` has no title of its own
         * (see `resolveBreadcrumbTrail`).
         */
        metadata?: {
            title: string;
        };
        /** Breadcrumb is used for the breadcrumb in the page */
        breadcrumb?: CommonBreadCrumbItem & {
            title?: string;
        };
        /**
         * UI is used for the page title, subtitle, and variant.
         *
         * Title resolution lives in `resolvePageHeader` (utils/navigation/route-header-resolution):
         * own `title` → async `context.ui.title` from `beforeLoad` → single-breadcrumb fallback →
         * inherited title (see `useTitleForChildren`/`useParentTitle` below).
         */
        ui?: {
            subtitle?: string;
            /**
             * Icon to display in the left navigation menu for this route.
             * Use a Lucide component from the ICONS registry (e.g., `ICONS.incident`).
             * Icons resolve up the parent chain when a route has none (see `resolveRouteIcon`).
             */
            icon?: LucideIcon;
            /**
             * Short description for menu/landing page display.
             * Used by configuration landing page to show descriptions for each menu item.
             */
            menuDescription?: string;
            /**
             * Controls horizontal width constraints of the page content.
             * - `'default'` - Centered container with responsive max-width
             * - `'compact'` - Narrow centered container for forms
             * - `'narrow'` - Extra-narrow centered column for single-column forms
             * - `'fullWidth'` - No width constraints (default behaviour)
             */
            widthVariant?: 'default' | 'compact' | 'narrow' | 'fullWidth';
            /**
             * Controls vertical height behaviour of the page content.
             * - `'auto'` - Natural height based on content (default)
             * - `'fit'` - Constrains to viewport height (h-full)
             */
            heightVariant?: 'fit' | 'auto';
            /**
             * When true, this route inherits its title AND subtitle from an ancestor: the nearest
             * non-overlay ancestor that defines a `ui.title`. Own `subtitle` wins over the
             * inherited one.
             */
            useParentTitle?: boolean;
            /**
             * Renders no page header title, for pages that draw their own H1 (record pages on
             * `EntityDetailsHeader`). Untitled descendants, such as the record's tab routes, stay
             * untitled too.
             */
            hideTitle?: boolean;
            disableLayoutSwitch?: boolean;
            /**
             * Tailwind class overrides appended to `PageLayout`'s outer wrapper, merged via `cn`
             * (tailwind-merge). Every non-overlay match contributes in parent→child order, so
             * later utilities resolve conflicts in their favour while non-conflicting utilities
             * compose. Pass `'p-0'` to drop the default `px-7 pt-7 pb-4`, `'pt-0'` for top only,
             * etc. Intended for two-column full-page shells where the page owns its own spacing.
             */
            className?: string;
        } & (
            | {
                  /**
                   * When true, descendant routes without a title of their own inherit THIS route's
                   * `title` and `subtitle` (which is why `title` is required alongside this flag).
                   * Children can override with their own `ui.title` or opt out with
                   * `useParentTitle: false`. Put the flag on the route whose title should
                   * propagate — typically a titled section route with tab/index children.
                   */
                  useTitleForChildren: true;
                  title: string;
              }
            | {
                  useTitleForChildren?: false;
                  title?: string;
              }
        );
        /** Roles are used to restrict access to the page */
        /**
         * @deprecated Use permissions instead
         */
        roles?: Role[];
        /** Modules are used to restrict access to the page */
        modules?: AppModule[];
        userFlags?: UserFlag[];
        /**
         * Feature flag key that must be enabled for this route to be accessible.
         * Uses the provider-agnostic feature flag system.
         */
        featureFlag?: string;
        restrictToEmployee?: boolean;
        /**
         * Console-only surface: denied to users whose roles grant no console workspace
         * access (a portal-only fleet user). Complements `permissions`, which cannot
         * express "any console role" without enumerating them (CUR-1049).
         */
        consoleWorkspace?: boolean;
        /**
         * Permissions gating this route: the user needs ANY ONE of the listed permissions.
         * Enforced by `useHasRouteAccess` for nav/tab visibility AND by `RouteAccessGuard`
         * for direct navigation (deep links render NotFound when the check fails). Client-side
         * UX only — the server remains the authoritative access boundary.
         */
        permissions?: Permission[];
        /**
         * Entity type for this route. Used by the general AI chat to understand
         * what entity the user is currently viewing (e.g., claim, incident).
         * Set this on detail routes to enable contextual AI assistance.
         */
        entity?: FormStateEntity;
    }
}

export function TanstackRouterProvider() {
    return (
        <RouterProvider
            router={router}
            context={{
                trpcUtils: trpcQueryUtils,
                featureFlagClient,
            }}
        />
    );
}
